import React from 'react';
import { Form, Input, Select, Button, Switch, Spin } from 'antd';
import {
  EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined,
  FolderOpenOutlined, TagOutlined, AppstoreOutlined, CheckCircleOutlined,
  CalendarOutlined, ClockCircleOutlined, UserOutlined,
} from '@ant-design/icons';
import GuardedButton from '../../components/guardedButton';
import { AiTwotoneFolderOpen } from "react-icons/ai";
import SpinnerLoader from '../../components/spinnerLoader';

interface CategoryDetailPanelProps {
  detail: any;
  loading: boolean;
  isEditing: boolean;
  canEdit: boolean;
  saving: boolean;
  form: any;
  role: string;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  onToggleStatus: (checked: boolean) => void;
  statusLoading?: boolean;
}

const fmtDateTime = (v: any) =>
  v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null;

const typeChipStyle = (type?: string): React.CSSProperties =>
  type === 'QUERY'
    ? { background: '#e6fffb', color: '#08979c', border: '1px solid #77797a',borderRadius: '5px' }
    : { background: '#f9f0ff', color: '#722ed1', border: '1px solid #77797a', borderRadius: '5px' };

const Field: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="cat-field">
      <span className="cat-field-label">{icon}{label}</span>
      <span className="cat-field-value">{isEmpty ? <span className="cat-muted">—</span> : value}</span>
    </div>
  );
};

const CategoryDetailPanel: React.FC<CategoryDetailPanelProps> = ({ detail, loading, isEditing, canEdit, saving, 
  form, role, onEdit, onCancel, onSave, onDelete, onToggleStatus, statusLoading,}) => {
    
  const active = !!detail?.isActive;
  const createdBy = detail?.createdByName ?? (typeof detail?.createdBy === 'object' ? detail?.createdBy?.name : detail?.createdBy);
  const updatedBy = detail?.updatedByName ?? (typeof detail?.updatedBy === 'object' ? detail?.updatedBy?.name : detail?.updatedBy);

  const statusPill = (
    <span className={`batch-status-pill batch-status-pill--${active ? 'active' : 'inactive'}`}
      style={{ border: "1px solid #77797a" }}>
      <span className="batch-status-dot" />{active ? 'Active' : 'Inactive'}
    </span>
  );

  return (
    <div className="cat-panel">
      <div className="cat-card">
        {/* Header */}
        <div className="cat-card-head">
          <div className="cat-head-left">
            <div className="cat-head-icon"><AiTwotoneFolderOpen size={35} /></div>
            <div className="cat-head-titles">
              <div className="cat-head-name">{detail?.categoryName || '—'}</div>
              <div className="cat-head-meta">

                {detail?.shortName && <span className="cat-head-code"><TagOutlined /> {detail.shortName}</span>}
              </div>
            </div>
          </div>

          <div className="cat-head-actions" onClick={(e) => e.stopPropagation()}>

            {!isEditing ? (
              <>
                {canEdit && (
                  <GuardedButton className="cat-btn cat-btn-edit" icon={<EditOutlined />} onClick={onEdit}>Edit</GuardedButton>
                )}
                {role !== 'STAFF' && <GuardedButton className="cat-btn cat-btn-delete" danger icon={<DeleteOutlined />} onClick={onDelete}>Delete</GuardedButton>}
              </>
            ) : (
              <>
                <Button className="cat-btn" icon={<CloseOutlined />} onClick={onCancel}>Cancel</Button>
                <Button className="cat-btn" type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>Save Changes</Button>
              </>
            )}
          </div>
        </div>
        <div className="cat-card-body">
          {loading ? (
            <div className="cat-loading"><Spin /></div>
          ) : (
            <>
              <div className="cat-section">
                {isEditing ? (
                  <Form form={form} layout="vertical" className="cat-form">
                    <div className="cat-grid cat-grid--3">
                      <Form.Item name="categoryName" label="Category Name" rules={[{ required: true, message: 'Please enter category name' }]}>
                        <Input placeholder="Enter category name" />
                      </Form.Item>
                      <Form.Item name="shortName" label="Short Name" rules={[{ required: true, message: 'Please enter short name' }, { max: 10, message: 'Maximum 10 characters allowed' }]}>
                        <Input placeholder="Enter short name" />
                      </Form.Item>
                      <Form.Item name="categoryType" label="Category Type" rules={[{ required: true, message: 'Please select category type' }]}>
                        <Select
                          placeholder="Select type"
                          options={[{ label: 'Service', value: 'SERVICE' }, { label: 'Query', value: 'QUERY' }]}
                        />
                      </Form.Item>
                      <Form.Item label="Status">
                        <Switch
                          checked={active}
                          loading={statusLoading}
                          onChange={onToggleStatus}
                          checkedChildren="Active"
                          unCheckedChildren="Inactive"
                        />
                      </Form.Item>
                    </div>
                  </Form>
                ) : (
                  <>
                    <div className="cat-grid cat-grid--3" style={{marginBottom: '8px'}}>
                      <Field icon={<FolderOpenOutlined />} label="Category:" value={detail?.categoryName} />
                      <Field icon={<TagOutlined />} label="Short Name :" value={detail?.shortName} />
                      <Field
                        icon={<AppstoreOutlined />}
                        label="Category Type :"
                        value={detail?.categoryType ? <span className="cat-chip" style={typeChipStyle(detail.categoryType)}>{detail.categoryType}</span> : null}
                      />
                      <Field icon={<CheckCircleOutlined />} label="Status : " value={statusPill} />
                    </div>
                    <div className="cat-section">
                      <div className="cat-grid cat-grid--3" style={{ borderTop: '1px solid #e5e8ee', paddingTop: '22px' }}>
                        <Field icon={<UserOutlined />} label="Created By :" value={createdBy} />
                        <Field icon={<CalendarOutlined />} label="Created At :" value={fmtDateTime(detail?.createdAt)} />
                        <Field icon={<UserOutlined />} label="Updated By :" value={updatedBy} />
                        <Field icon={<ClockCircleOutlined />} label="Updated At :" value={fmtDateTime(detail?.updatedAt)} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryDetailPanel;
